<!--
 * COPYRIGHT Transtron Inc 2023
 *
 * 機能名：端末情報一覧
 *
 * 作成者：2024/04/01 ノア）許
 * 最終修正者：2024/04/01 ノア）許
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button
            class="backButton"
            @click="onBackButtonClick"
            v-tooltip="{
              content: `${$t('gpsTracker.tooltipCommon.companyInfo.back')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('gpsTracker.sigfoxInfo.title') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- データ -->
    <section class="tti-padding-content">
      <div class="tti-content-title company-content">
        <div class="company-content-code">
          <span>{{ $t('gpsTracker.sigfoxInfo.companyCode') }}</span>
          <el-select
            v-model="companyCode"
            filterable
            remote
            @focus="handleFocus"
            placeholder=""
            :remote-method="remoteMethodCompanyCode"
            clearable
            @change="handleSelectChange"
            v-tooltip="{
              content: `${$t('gpsTracker.tooltipCommon.sigfoxInfo.tbCompanyCode')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
            ><el-option v-for="item in options" :key="item.value" :label="item.value + ' ' + item.label" :value="item">
            </el-option
          ></el-select>
        </div>
        <div class="company-content-name">
          <span>{{ $t('gpsTracker.sigfoxInfo.companyName') }}</span>
          <el-select
            v-model.trim="companyName"
            filterable
            remote
            @focus="handleFocus"
            placeholder=""
            :remote-method="remoteMethodCompanyName"
            clearable
            @change="handleSelectChange"
            v-tooltip="{
              content: `${$t('gpsTracker.tooltipCommon.sigfoxInfo.tbCompanyName')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
            ><el-option v-for="item in options" :key="item.value" :label="item.value + ' ' + item.label" :value="item">
            </el-option
          ></el-select>
        </div>

        <span>{{ $t('gpsTracker.sigfoxInfo.sigfoxId') }}</span>
        <div class="searchBox">
          <el-input
            v-model.trim="sigfoxSearchText"
            v-tooltip="{
              content: `${$t('gpsTracker.tooltipCommon.sigfoxInfo.tbSigfoxID')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          ></el-input>
          <!-- 検索 -->
          <el-button
            class="tti-btn-default tti-btn-002"
            @click="onSearchButtonClick"
            v-tooltip="{
              content: `${$t('gpsTracker.tooltipCommon.sigfoxInfo.btnSearch')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            {{ $t('buttons.search') }}
          </el-button>
        </div>
      </div>
      <div class="functionArea">
        <div class="tti-content-title" v-if="totalCount > fixCount">
          <span>{{ $t('gpsTracker.sigfoxInfo.countTitle', [totalCount, fixCount]) }}</span>
        </div>
        <el-pagination
          background
          layout="prev, pager, next"
          :total="tableData.length"
          :page-size="visablePageSize"
          :hide-on-single-page="true"
          :current-page.sync="currentPage"
          @current-change="onCurrentPageChange"
        >
        </el-pagination>
        <div class="pageSearchBox">
          <el-input v-model.trim="searchText" onkeyup="this.value=this.value.replace(/[,]/g,'')">
            <el-button slot="append" icon="el-icon-search" @click="onSearchBoxClick"></el-button>
          </el-input>
        </div>
      </div>
      <el-form :model="form" ref="form">
        <el-table
          ref="tableRef"
          :data="visableTableData"
          height="calc(100vh - 250px)"
          :border="true"
          :stripe="true"
          header-row-class-name="tti-master-table-header"
          :row-class-name="tableRowClassName"
          :empty-text="$t('messages.common.emptyTableData')"
          highlight-current-row
        >
          <!-- 会社コード -->
          <el-table-column prop="companyCode" :label="$t('gpsTracker.sigfoxInfo.companyCode')" width="130">
          </el-table-column>
          <!-- 会社名 -->
          <el-table-column prop="companyName" :label="$t('gpsTracker.sigfoxInfo.companyName')" width="300">
          </el-table-column>
          <!-- 営業所名 -->
          <el-table-column
            prop="businessOfficeName"
            :label="$t('gpsTracker.sigfoxInfo.businessOfficeName')"
            width="300"
          >
          </el-table-column>
          <!-- GPSトラッカーID -->
          <el-table-column prop="sigfoxId" :label="$t('gpsTracker.sigfoxInfo.sigfoxId')" width="160"
            ><template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].sigfoxId'">
                <el-input
                  class="sigfoxInput"
                  :id="'tableData[' + scope.$index + '].sigfoxId'"
                  :disabled="!canCodeChange(scope.row.dataType)"
                  v-model.trim="scope.row.sigfoxId"
                  size="small"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 利用開始日 -->
          <el-table-column prop="startUseDate" :label="$t('gpsTracker.sigfoxInfo.startDate')" width="180">
          </el-table-column>
          <!-- 節電モード -->
          <el-table-column prop="powerSavingMode" :label="$t('gpsTracker.sigfoxInfo.powerSavingMode')" width="200">
          </el-table-column>
          <!-- 最終更新日 -->
          <el-table-column prop="lastUpdateDate" :label="$t('gpsTracker.sigfoxInfo.lastUpdateDate')" width="170">
          </el-table-column>
          <!-- 利用状況 -->
          <el-table-column prop="usageStatus" :label="$t('gpsTracker.sigfoxInfo.usageStatus')" width="100">
          </el-table-column>
          <!-- 累計通信回数 -->
          <el-table-column prop="sendCount" :label="$t('gpsTracker.sigfoxInfo.sendCount')" width="313">
          </el-table-column>
        </el-table>
      </el-form>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Ref } from 'vue-property-decorator'
import { Form, Table } from 'element-ui'
import { ToolCompanyMasterData, ToolSigfoxInfoSearch } from '@/models'

import { getToolCompanyInfoData, getToolCompanySigfoxInfoData } from '@/store/apis'
import MST_BASE, { FormItemType } from '@/views/master/MST_BASE.vue'

@Component({
  name: 'TOOL_SIGFOX_INFO'
})
export default class TOOL_SIGFOX_INFO extends MST_BASE {
  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form
  @Ref('tableRef') readonly tableRef!: Table

  /**
   * 画面ID
   */
  pageId = 'TOOL_SIGFOX_INFO'
  /**
   * 第一列の項目（Focus用）
   */
  firstField = 'sigfoxId'

  /**
   * 契約会社コード.
   */
  companyCode = ''
  /**
   * 契約会社名
   */
  companyName = ''
  /**
   * 検索文字列
   */
  sigfoxSearchText = ''

  /**
   * 合計数
   */
  totalCount = 0

  /**
   * 固定表示件数
   */
  fixCount = 1000

  /**
   * 会社データ
   */
  companyData: Array<ToolCompanyMasterData> = []

  /**
   * 検索用オプション
   */
  options: Array<ToolSigfoxInfoSearch> = []
  /**
   * 選択したオプション
   */
  value = []
  /**
   * オプションリスト
   */
  list: Array<ToolSigfoxInfoSearch> = []

  /**
   * 初期化。
   */
  async mounted(): Promise<void> {
    this.$log.debug('TOOL_SIGFOX_INFO > mounted')
    await this.initData()
    this.initVisableData()
    this.initSearchOption()
    this.initFormRef(this.formRef)
    this.initTableRef(this.tableRef)
    this.initSearchTargets()
  }

  /**
   * フィルタリング：会社コード。
   *
   * @param {string} query 検索条件
   */
  remoteMethodCompanyCode(query: string): void {
    if (query !== '') {
      this.options = this.list.filter(item => {
        return item.value.indexOf(query) > -1
      })
    } else {
      this.options = []
    }
  }

  /**
   * フィルタリング：会社名。
   *
   * @param {string} query 検索条件
   */
  remoteMethodCompanyName(query: string): void {
    if (query !== '') {
      this.options = this.list.filter(item => {
        return item.label.indexOf(query) > -1
      })
    } else {
      this.options = []
    }
  }

  /**
   * ハンドル選択の変更
   *
   * @param {ToolSigfoxInfoSearch} value 値
   */
  handleSelectChange(value: ToolSigfoxInfoSearch): void {
    this.companyCode = value.value
    this.companyName = value.label
  }

  /**
   * フォーカス取得会社
   */
  handleFocus(): void {
    this.options = this.list
  }

  /**
   * 検索するターゲットの初期化
   */
  initSearchTargets(): void {
    this.searchTargets = [
      {
        type: FormItemType.Input,
        field: 'sigfoxId'
      }
    ]
  }

  /**
   * 検索条件のテンプレートを初期化する
   *
   * @returns {void}
   */
  async initSearchOption(): Promise<void> {
    try {
      const param = {
        nameOrCode: ''
      }
      this.companyData = (await getToolCompanyInfoData(param)).list as Array<ToolCompanyMasterData>
      this.list = this.companyData.map(item => {
        return { value: item.companyCode, label: item.companyName }
      })
    } catch (e) {
      this.$log.debug('TOOL_SIGFOX_INFO > initSearchOption: e=', e)
      throw e
    }
  }

  /**
   * 画面を初期化する。
   *
   * @returns {void}
   */
  async initData(): Promise<void> {
    try {
      const param = {
        companyCode: '',
        companyName: '',
        sigfoxId: []
      }

      const res = await getToolCompanySigfoxInfoData(param)

      this.tableData = res.list

      this.totalCount = res.count
    } catch (e) {
      this.$log.debug('TOOL_SIGFOX_INFO > initData: e=', e)
      throw e
    }
  }

  /**
   * 戻るボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onBackButtonClick(): void {
    this.$log.debug(`TOOL_SIGFOX_INFO > onBackButtonClick`)
    this.$router.back()
  }

  /**
   * 検索BOXをクリックしたときの処理
   *
   * @returns {void}
   */
  async onSearchButtonClick(): Promise<void> {
    this.$log.debug(`TOOL_SIGFOX_INFO > onSearchButtonClick`)
    this.initSearchBox()

    const textList = this.sigfoxSearchText.split(',')
    const sigfoxIdList: string[] = []
    textList.forEach(sigfoxId => {
      if (sigfoxId) {
        if (sigfoxId.length < 8) {
          sigfoxId = sigfoxId.padStart(8, '0')
        }
        sigfoxIdList.push(sigfoxId)
      }
    })
    const param = {
      companyCode: this.companyCode,
      companyName: this.companyName,
      sigfoxId: sigfoxIdList
    }

    const res = await getToolCompanySigfoxInfoData(param)
    this.tableData = res.list
    this.totalCount = res.count
    this.initVisableData()
  }

  /**
   * インスタンスが破棄された後にフック処理
   *
   * @returns {void}
   */
  doDestroy(): void {
    this.$log.debug('TOOL_SIGFOX_INFO > doDestroy')
  }
}
</script>

<style lang="scss" scoped>
.el-select-dropdown__item {
  width: 200px;
}

.sigfoxInput .el-input__inner {
  border: none;
}

.company-content {
  display: flex;
  align-items: center;
  column-gap: 50px;
  margin-bottom: 20px;

  .company-content-code {
    display: flex;
    align-items: center;

    span {
      width: 140px;
    }
  }

  .company-content-name {
    display: flex;
    align-items: center;

    span {
      width: 140px;
    }
  }

  .searchBox {
    display: flex;
    align-items: center;

    span {
      width: 180px;
    }
  }

  ::v-deep .el-input__inner {
    height: 35px;
    line-height: 35px;
    padding: 0 8px;
  }

  .tti-btn-default {
    margin-left: 40px;
  }
}

::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-main-header-shadow {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.functionArea {
  margin-bottom: 20px;
  display: flex;
  justify-content: flex-end;

  .pageSearchBox {
    margin-left: 16px;

    ::v-deep .el-input__inner {
      height: 32px;
    }

    ::v-deep .el-input-group__append {
      height: 30px;
      padding: 0;
    }

    ::v-deep .el-button--default {
      height: 30px;
      padding: 6px 10px;
      margin: 0;
    }
  }

  .tti-content-title {
    margin: 0;
    margin-right: auto;
  }
}
</style>
